<template>
  <div class="memory-game">
    <LevelComplete
      v-if="isGameComplete"
      message="Congratulations!"
    />
    <div v-if="!isGameComplete">
      <div class="grid">
        <div
          v-for="(item, index) in shuffledItems"
          :key="index"
          class="item-card"
          :class="{
            flipped:
              flippedIndices.includes(index) || matchedIndices.includes(index),
            matched: matchedIndices.includes(index),
          }"
          @click="handleCardClick(index)"
        >
          <div class="card-front">
            ?
          </div>
          <div class="card-back">
            <!-- Display domino dots for dots items -->
            <div
              v-if="selectedCategory === 'numbers' && isDotItem(item)"
              class="domino"
            >
              <div
                v-for="dot in getDotsArray(item)"
                :key="dot"
                class="dot"
                :class="'dot-' + dot"
              />
            </div>
            <!-- Display the number for number items -->
            <div
              v-else-if="selectedCategory === 'numbers'"
              class="number-display"
            >
              {{ item }}
            </div>
            <!-- For 'words', 'animals', and 'body' categories, display items appropriately -->
            <div
              v-else-if="
                selectedCategory === 'words' ||
                  selectedCategory === 'animals' ||
                  selectedCategory === 'body'
              "
              class="word-display"
            >
              <div
                v-if="isEmoji(item)"
                class="emoji"
              >
                {{ item }}
              </div>
              <div
                v-else
                class="word"
              >
                {{ item }}
              </div>
            </div>
            <!-- Display the item for other categories -->
            <div v-else>
              {{ item }}
            </div>
          </div>
        </div>
      </div>

      <div class="progress">
        <p>Matches found: {{ matchedCount }}/{{ maxCount }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import { speechMixin } from "@/mixins/speechMixin.js";

import LevelComplete from "../LevelComplete.vue";

export default {
  components: {
    LevelComplete,
  },
  mixins: [speechMixin],
  props: {
    selectedCategory: {
      type: String,
      default: "vowels",
    },
  },
  data() {
    return {
      categories: {
        vowels: { A: "a", E: "e", I: "i", O: "o", U: "u" },
        alphabet: "ABCDEFGHIJKLMNOPQRSTUVWXYZ"
          .split("")
          .reduce((acc, letter) => {
            acc[letter] = letter.toLowerCase();
            return acc;
          }, {}),
        numbers: {}, // We'll handle numbers differently
        words: {
          Sun: "☀️",
          Moon: "🌙",
          Star: "⭐",
          Heart: "❤️",
          Apple: "🍎",
          Butterfly: "🦋",
          Fire: "🔥",
          Water: "💧",
          Tree: "🌳",
          Flower: "🌸",
        },
        animals: {
          Cat: "🐱",
          Dog: "🐶",
          Lion: "🦁",
          Elephant: "🐘",
          Monkey: "🐒",
        },
        body: {
          Head: "👤",
          Hand: "✋",
          Foot: "🦶",
          Eye: "👁️",
          Nose: "👃",
          Ear: "👂",
          Mouth: "👄",
          Tooth: "🦷",
          Leg: "🦵",
          Brain: "🧠",
        },
      },
      items: [],
      shuffledItems: [],
      flippedIndices: [],
      matchedIndices: [],
      matchedCount: 0,
      maxCount: 5,
      isGameComplete: false,
    };
  },
  computed: {
    currentCategory() {
      return this.categories[this.selectedCategory];
    },
  },
  mounted() {
    this.setupVoices();
    this.setupGame();
  },
  methods: {
    setupGame() {
      if (this.selectedCategory === "numbers") {
        // For numbers, create pairs of numbers and their dot representations
        this.items = [];
        for (let i = 1; i <= this.maxCount; i++) {
          const numberItem = i.toString();
          const dotItem = `dots${i}`;
          this.items.push(numberItem, dotItem);
        }
      } else {
        const keys = Object.keys(this.currentCategory);
        const selectedKeys = keys.slice(0, this.maxCount);
        const pairs = selectedKeys.map((item) => [
          item,
          this.currentCategory[item],
        ]);
        this.items = pairs.flat();
      }

      this.shuffledItems = this.items.sort(() => Math.random() - 0.5);
      this.flippedIndices = [];
      this.matchedIndices = [];
      this.matchedCount = 0;
      this.isGameComplete = false;
      this.speakOutLoud("Find the pairs!");
    },
    handleCardClick(index) {
      if (
        this.flippedIndices.length < 2 &&
        !this.flippedIndices.includes(index) &&
        !this.matchedIndices.includes(index)
      ) {
        this.flippedIndices.push(index);

        // Speak the item when the card is clicked
        const item = this.shuffledItems[index];
        const message = this.getSpeakableItem(item);
        this.speakOutLoud(message);

        if (this.flippedIndices.length === 2) {
          const [firstIndex, secondIndex] = this.flippedIndices;
          const firstItem = this.shuffledItems[firstIndex];
          const secondItem = this.shuffledItems[secondIndex];

          if (this.isMatchingPair(firstItem, secondItem)) {
            this.matchedIndices.push(...this.flippedIndices);
            this.matchedCount++;
            this.playSound(
              "https://commondatastorage.googleapis.com/codeskulptor-assets/week7-brrring.m4a"
            );
            if (this.matchedCount === this.maxCount) {
              this.isGameComplete = true;

              this.playSound(
                "https://codeskulptor-demos.commondatastorage.googleapis.com/descent/gotitem.mp3"
              );

              this.speakOutLoud("Congratulations!");

              setTimeout(() => {
                const categoryRoute =
                  this.selectedCategory === "vowels" ||
                  this.selectedCategory === "alphabet"
                    ? "/letters"
                    : `/${this.selectedCategory}`;
                this.$router.push(categoryRoute);
              }, 4000);
            }
          }

          setTimeout(() => {
            this.flippedIndices = [];
          }, 1000);
        }
      }
    },
    isMatchingPair(firstItem, secondItem) {
      if (this.selectedCategory === "numbers") {
        const numberPattern = /^\d+$/;
        const dotsPattern = /^dots(\d+)$/;

        const firstIsNumber = numberPattern.test(firstItem);
        const secondIsNumber = numberPattern.test(secondItem);
        const firstIsDots = dotsPattern.test(firstItem);
        const secondIsDots = dotsPattern.test(secondItem);

        if (
          (firstIsNumber && secondIsDots) ||
          (firstIsDots && secondIsNumber)
        ) {
          const number = firstIsNumber
            ? parseInt(firstItem)
            : parseInt(secondItem);
          const dotsMatch = firstIsDots
            ? parseInt(firstItem.match(dotsPattern)[1])
            : parseInt(secondItem.match(dotsPattern)[1]);
          return number === dotsMatch;
        } else {
          return false;
        }
      } else if (
        this.selectedCategory === "words" ||
        this.selectedCategory === "animals" ||
        this.selectedCategory === "body"
      ) {
        const currentPairs = this.currentCategory;
        return (
          currentPairs[firstItem] === secondItem ||
          currentPairs[secondItem] === firstItem
        );
      } else {
        return firstItem.toLowerCase() === secondItem.toLowerCase();
      }
    },
    isDotItem(item) {
      return /^dots\d+$/.test(item);
    },
    getDotsArray(item) {
      if (this.isDotItem(item)) {
        const numDots = parseInt(item.replace("dots", ""));
        return Array.from({ length: numDots }, (_, i) => i + 1);
      } else {
        return [];
      }
    },
    playSound(url) {
      const audio = new Audio(url);
      audio.play();
    },
    isEmoji(item) {
      const emojiValues = Object.values(this.currentCategory);
      return emojiValues.includes(item);
    },
    getSpeakableItem(item) {
      if (this.selectedCategory === "numbers") {
        // For numbers category
        if (/^\d+$/.test(item)) {
          // It's a number
          return item;
        } else if (/^dots\d+$/.test(item)) {
          // It's a dots item
          const numDots = parseInt(item.replace("dots", ""));
          return numDots.toString();
        }
      } else if (
        this.selectedCategory === "words" ||
        this.selectedCategory === "animals" ||
        this.selectedCategory === "body"
      ) {
        // For words, animals, and body categories
        if (this.isEmoji(item)) {
          // It's an emoji, find the corresponding word
          const word = Object.keys(this.currentCategory).find(
            (key) => this.currentCategory[key] === item
          );
          return word || "Unknown";
        } else {
          // It's a word
          return item;
        }
      } else {
        // For other categories (vowels, alphabet)
        return item.toUpperCase();
      }
    },
  },
};
</script>

<style scoped>
/* Ensure the grid container takes full width */
.grid {
  width: 90%;
  max-width: 1000px;
  margin: auto;
  margin-bottom: 2rem;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(80px, 1fr));
  gap: 0.5rem;
}

/* Responsive adjustments for the grid */
@media (min-width: 600px) {
  .grid {
    gap: 1rem;
  }
}

@media (min-width: 992px) {
  .grid {
    gap: 1.5rem;
  }
}

/* Adjust the item-card to use relative units and maintain aspect ratio */
.item-card {
  cursor: pointer;
  perspective: 1000px;
  position: relative;
  width: 100%;
  padding-top: 150%; /* 2:3 Aspect Ratio */
  transform-style: preserve-3d;
  transition: transform 0.6s;
}

.item-card.flipped {
  transform: rotateY(180deg);
}

.item-card.matched .card-back {
  background-color: lightgreen;
}

/* Position the card faces absolutely */
.card-front,
.card-back {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  backface-visibility: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 10px;
  font-size: 2rem;
}

/* Adjust font size for smaller screens */
@media (max-width: 599px) {
  .card-front,
  .card-back {
    font-size: 1.5rem;
  }
}

.card-front {
  background-color: #ff4081;
}

.card-back {
  background-color: #81d4fa;
  transform: rotateY(180deg);
}

/* Progress text styling */
.progress {
  text-align: center;
  font-size: 1.5rem;
  margin-top: 1rem;
}

/* Adjust font size for smaller screens */
@media (max-width: 599px) {
  .progress {
    font-size: 1.2rem;
  }
}

/* Number display styling */
.number-display {
  font-size: 2.5rem;
}

/* Adjust number display for smaller screens */
@media (max-width: 599px) {
  .number-display {
    font-size: 2rem;
  }
}

/* Domino styling */
.domino {
  position: relative;
  width: 80%;
  height: 80%;
  background-color: #fff;
  border: 2px solid #000;
  border-radius: 10px;
}

/* Dot styling */
.dot {
  position: absolute;
  width: 15%;
  height: 15%;
  background-color: #000;
  border-radius: 50%;
}

/* Adjust dot positions */
.dot-1 {
  top: 42.5%;
  left: 42.5%;
}

.dot-2 {
  top: 20%;
  left: 20%;
}

.dot-3 {
  top: 65%;
  left: 65%;
}

.dot-4 {
  top: 20%;
  left: 65%;
}

.dot-5 {
  top: 65%;
  left: 20%;
}

/* Word display styling */
.word-display .word {
  font-size: 2rem;
}

/* Emoji display styling */
.word-display .emoji {
  font-size: 3rem;
}

/* Adjust word and emoji sizes for smaller screens */
@media (max-width: 599px) {
  .word-display .word {
    font-size: 1.5rem;
  }
  .word-display .emoji {
    font-size: 2.5rem;
  }
}
</style>